import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Ban } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const SUSPENSION_DAYS = 7;

export default function UnpaidAlertsCard() {
  const [list, setList] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/unpaid-alerts").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const suspend = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.restaurant_id}/suspend`, { days: SUSPENSION_DAYS, reason: "Ordini aggiudicati non pagati" });
      toast.success(`${target.name} sospeso per ${SUSPENSION_DAYS} giorni`);
      setTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (list === null) return null;
  return (
    <div className={`ga-card p-5 ${list.length ? "border-2 border-amber-400" : ""}`} data-testid="unpaid-alerts-card">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Ristoranti con ordini non pagati ({list.length})</div>
      <div className="mt-1 text-xs text-slate-500">Un ordine aggiudicato senza bonifico dichiarato entro 48 ore viene annullato e conta come mancato pagamento. Qui compaiono i ristoranti con almeno 2 ordini scaduti negli ultimi 30 giorni.</div>
      {list.length === 0 && <div className="mt-2 text-sm text-slate-500" data-testid="unpaid-alerts-empty">Nessun ristorante da segnalare.</div>}
      <div className="mt-3 space-y-2">
        {list.map(r => (
          <div key={r.restaurant_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3 text-sm" data-testid={`unpaid-alert-${r.restaurant_id}`}>
            <div className="min-w-0">
              <span className="font-semibold text-slate-900">{r.name}</span> <span className="text-xs font-mono text-slate-500">({r.email})</span>
              <div className="text-xs text-slate-600 mt-0.5">{r.count} ordini scaduti senza pagamento · ultimo il {new Date(r.last_at).toLocaleString("it-IT")}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{r.orders.join(" · ")}</div>
            </div>
            {r.suspended_until
              ? <span className="text-xs font-semibold text-slate-700">Sospeso fino al {new Date(r.suspended_until).toLocaleString("it-IT")}</span>
              : <button onClick={() => setTarget(r)} className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-semibold" data-testid={`unpaid-suspend-${r.restaurant_id}`}><Ban className="w-3 h-3" /> Sospendi</button>}
          </div>
        ))}
      </div>
      <Dialog open={!!target} onOpenChange={(open) => { if (!open) setTarget(null); }}>
        <DialogContent data-testid="unpaid-suspend-dialog">
          <DialogHeader>
            <DialogTitle>Sospendere {target?.name}?</DialogTitle>
            <DialogDescription>Per {SUSPENSION_DAYS} giorni il ristorante non potrà creare ordini né scegliere offerte. Potrà ancora pagare gli ordini già aggiudicati e annullare le aste aperte. Puoi revocare la sospensione in qualsiasi momento.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTarget(null)}>Indietro</Button>
            <Button onClick={suspend} disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="unpaid-suspend-confirm">Sospendi {SUSPENSION_DAYS} giorni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
